import { CollectionOutlineIcon, CreditCardOutlineIcon } from 'assets/svgs';
import { Text } from 'components';
import { useState, useEffect, useMemo } from 'react';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import { SpacecraftCardList } from 'components/Dashboard/monitoring/SpacecraftCardList';
import { EarthOrbitSection } from 'components/Dashboard/monitoring/EarthOrbitSection';
import { PeriodSelector } from 'components/Dashboard/PaymentAnalytics/PeriodSelector';
import { OverviewSection } from 'components/Dashboard/PaymentAnalytics/OverviewSection';
import { PriceMonitorSection } from 'components/Dashboard/PaymentAnalytics/PriceMonitorSection';
import { cn } from 'utils';
import { LatestScheduleSection } from 'components/Dashboard/monitoring/LatestScheduleSection';
import { DownlinkFileSection } from 'components/Dashboard/monitoring/DownlinkFileSection';
import { getDecodeCookie } from 'common/Cookie';
import axios from 'common/AxiosAuth';
import dayjs from 'dayjs';

type Currency = 'KRW' | 'USD';
type CountryCode = 'KR' | 'OTHER';
type PeriodCode = '6M' | '1M' | '1W'; // 라벨이 아닌 코드로 고정

const SYMBOL: Record<Currency, string> = { KRW: '₩', USD: '$' };
const formatMoney = (value: number, currency: Currency) =>
  currency === 'KRW'
    ? value.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })
    : value.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

// ▼ 코드 기준의 날짜 범위 계산(우측 캘린더/요청 파라미터에 사용)
function rangeByPeriod(code: PeriodCode): [string, string] {
  const end = dayjs().format('YYYY-MM-DD') + ' 23:59:59';
  const startBase =
    code === '1W' ? dayjs().subtract(1, 'week') :
    code === '1M' ? dayjs().subtract(1, 'month') :
    dayjs().subtract(6, 'months'); // 6M
  const start = startBase.format('YYYY-MM-DD') + ' 00:00:00';
  return [start, end];
}

const Dashboard = () => {
  const { t, i18n } = useTranslation();

  const [activeTab, setActiveTab] = useState(0);

  // ▼ 기간 상태는 항상 코드값으로 관리 (i18n 영향 없음)
  const [selectedPeriod, setSelectedPeriod] = useState<PeriodCode>('6M');

  const getInitialDateRange = () => rangeByPeriod('6M');
  const [selectedDateRange, setSelectedDateRange] = useState<string[]>(getInitialDateRange);

  // 서버에서 내려오는 통화/국가 (UI 포맷용 기본 통화)
  const [memberCurrency, setMemberCurrency] = useState<Currency>('USD');
  const [memberCountry, setMemberCountry] = useState<CountryCode>('OTHER');

  // 통계 데이터(금액은 서버가 제공하는 통화 기준)
  const [analyticsData, setAnalyticsData] = useState<any>(null);

  // 1) 회원 통화/국가 불러오기 (UI 표기 기본값)
  useEffect(() => {
    const fetchMemberCurrency = async () => {
      try {
        const res = await axios.get('/api/member/me/currency');
        const { countryCode, currency } = res.data as {
          countryCode: CountryCode;
          currency: Currency;
        };
        setMemberCountry(countryCode);
        setMemberCurrency(currency);
      } catch {
        setMemberCountry('OTHER');
        setMemberCurrency('USD');
      }
    };
    fetchMemberCurrency();
  }, []);

  // 2) 기간 변경 시 우측 캘린더 범위도 함께 갱신
  useEffect(() => {
    setSelectedDateRange(rangeByPeriod(selectedPeriod));
  }, [selectedPeriod]);

  // 3) 통계 데이터 불러오기
  const fetchAnalytics = async () => {
    try {
      // period는 코드값 그대로 사용
      const params: any = { period: selectedPeriod };
      if (selectedDateRange && selectedDateRange.length === 2) {
        params.startDate = selectedDateRange[0];
        params.endDate = selectedDateRange[1];
      }
      const res = await axios.get('/api/confirm/analytics', { params });
      setAnalyticsData(res.data);
    } catch (err) {
      console.error('결제 통계 가져오기 실패:', err);
    }
  };

  useEffect(() => {
    fetchAnalytics();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedPeriod, selectedDateRange]);

  // 4) 데이터의 통화(축/툴팁 표기용): 서버 응답에 currency가 있으면 그걸 우선, 없으면 회원 통화
  const datasetCurrency: Currency = useMemo(() => {
    const c = analyticsData?.currency;
    return c === 'KRW' || c === 'USD' ? c : memberCurrency;
  }, [analyticsData?.currency, memberCurrency]);

  const symbol = SYMBOL[datasetCurrency];

  // 5) Overview 카드 데이터(문자열로 포맷)
  const overviewData =
    analyticsData
      ? [
          {
            title: t('dashboard.paymentAnalytics.overview.totalPayments'),
            value: `${analyticsData.totalCount ?? 0}${t('dashboard.paymentAnalytics.overview.countUnit')}`,
            bgColor: 'bg-blue-50',
            iconColor: 'text-blue-500',
            icon: '✓'
          },
          {
            title: t('dashboard.paymentAnalytics.overview.totalAmount'),
            value: `${symbol}${formatMoney(Number(analyticsData.totalAmount ?? 0), datasetCurrency)}`,
            bgColor: 'bg-orange-50',
            iconColor: 'text-orange-500',
            icon: '$'
          },
          {
            title: t('dashboard.paymentAnalytics.overview.pending'),
            value: `${analyticsData.pendingCount ?? 0}${t('dashboard.paymentAnalytics.overview.countUnit')}`,
            amount: `${symbol}${formatMoney(Number(analyticsData.pendingAmount ?? 0), datasetCurrency)}`,
            bgColor: 'bg-gray-50',
            iconColor: 'text-gray-500',
            icon: ''
          },
          {
            title: t('dashboard.paymentAnalytics.overview.completed'),
            value: `${analyticsData.completedCount ?? 0}${t('dashboard.paymentAnalytics.overview.countUnit')}`,
            amount: `${symbol}${formatMoney(Number(analyticsData.completedAmount ?? 0), datasetCurrency)}`,
            bgColor: 'bg-gray-50',
            iconColor: 'text-gray-500',
            icon: ''
          },
          {
            title: t('dashboard.paymentAnalytics.overview.cancellationReq'),
            value: `${analyticsData.canceledCount ?? 0}${t('dashboard.paymentAnalytics.overview.countUnit')}`,
            amount: `${symbol}${formatMoney(Number(analyticsData.canceledAmount ?? 0), datasetCurrency)}`,
            bgColor: 'bg-gray-50',
            iconColor: 'text-gray-500',
            icon: ''
          },
          {
            title: t('dashboard.paymentAnalytics.overview.refunded'),
            value: `${analyticsData.refundedCount ?? 0}${t('dashboard.paymentAnalytics.overview.countUnit')}`,
            amount: `${symbol}${formatMoney(Number(analyticsData.refundedAmount ?? 0), datasetCurrency)}`,
            bgColor: 'bg-gray-50',
            iconColor: 'text-gray-500',
            icon: ''
          }
        ]
      : [];

  // 6) 차트 데이터
  const satelliteData =
    analyticsData?.satelliteCosts?.map((item: any) => ({
      name: item.name,
      value: Number(item.value) || 0,
      color: '#007aff'
    })) || [];

  const antennaData =
    analyticsData?.antennaUsageRatios?.map((item: any) => {
      const total = analyticsData.antennaUsageRatios.reduce(
        (sum: number, d: any) => sum + Number(d.value || 0),
        0
      );
      const pct = total ? (Number(item.value || 0) / total) * 100 : 0;
      return { name: item.name, value: Number(pct.toFixed(2)), color: '#ff9500' };
    }) || [];

  const getColorByMethod = (method: string) => {
    switch (method) {
      case 'FREE':
        return '#ff9500';
      case 'CARD':
        return '#5856d5';
      case 'BANK_TRANSFER':
        return '#04c7be';
      default:
        return '#ccc';
    }
  };

  const paymentMethodData =
    analyticsData?.methodUsageRatios?.map((item: any) => {
      const total = analyticsData.methodUsageRatios.reduce(
        (sum: number, d: any) => sum + Number(d.value || 0),
        0
      );
      const pct = total ? (Number(item.value || 0) / total) * 100 : 0;
      const key = String(item.name || '').toLowerCase();
      return {
        name: t(`dashboard.paymentAnalytics.paymentMethods.${key}`),
        percentage: Number(pct.toFixed(2)),
        color: getColorByMethod(item.name)
      };
    }) || [];

  const userInfo = getDecodeCookie('accessToken');
  const userName = userInfo?.name || 'User';

  const [latestSpacecrafts, setLatestSpacecrafts] = useState<any[]>([]);
  const [nextScheduleDays, setNextScheduleDays] = useState<number | null>(null);

  const fetchSpacecraftData = async () => {
    try {
      const spacecraftRes = await axios.get('/api/link/listAll');
      const spacecraftList = spacecraftRes.data;
      const filteredSpacecrafts = spacecraftList
        .filter((spacecraft: any) => spacecraft.tleFirst && spacecraft.tleSecond)
        .sort(
          (a: any, b: any) =>
            new Date(b.createdDate || b.registeredAt || 0).getTime() -
            new Date(a.createdDate || a.registeredAt || 0).getTime()
        );
      setLatestSpacecrafts(filteredSpacecrafts);
    } catch (error) {
      console.error('위성 데이터 가져오기 실패:', error);
      setNextScheduleDays(null);
    }
  };

  const fetchNextSchedule = async () => {
    try {
      const scheduleRes = await axios.get('/api/dashboard/schedule?limit=10');
      const schedules = scheduleRes.data.data;

      if (schedules && schedules.length > 0) {
        const now = dayjs().utc();
        let minDays = Infinity;

        schedules.forEach((schedule: any) => {
          if (schedule.startTime) {
            const scheduleDate = dayjs.utc(schedule.startTime);
            if (scheduleDate.isAfter(now)) {
              const daysDiff = scheduleDate.diff(now, 'day');
              if (daysDiff < minDays) minDays = daysDiff;
            }
          }
        });

        setNextScheduleDays(minDays !== Infinity ? minDays : null);
      } else {
        setNextScheduleDays(null);
      }
    } catch (error) {
      console.error('스케줄 데이터 가져오기 실패:', error);
      setNextScheduleDays(null);
    }
  };

  useEffect(() => {
    fetchSpacecraftData();
    fetchNextSchedule();
  }, []);

  // ▼ 표시용 라벨(언어에 따라 변함). 내부 로직은 계속 코드 사용.
  const isKo = i18n.language?.startsWith('ko');
  const periodLabels = useMemo(
    () => ({
      '6M': isKo ? t('dashboard.paymentAnalytics.periodOptions.sixMonths') : t('dashboard.paymentAnalytics.periodOptions.sixMonths'),
      '1M': isKo ? t('dashboard.paymentAnalytics.periodOptions.oneMonth')  : t('dashboard.paymentAnalytics.periodOptions.oneMonth'),
      '1W': isKo ? t('dashboard.paymentAnalytics.periodOptions.oneWeek')   : t('dashboard.paymentAnalytics.periodOptions.oneWeek')
    }),
    [i18n.language, t]
  );

  // PeriodSelector는 라벨을 주고/받으므로, 경계에서 라벨<->코드 변환만 수행
  const selectedPeriodLabel = periodLabels[selectedPeriod];

  // 같은 버튼 재클릭 가드 포함
  const handlePeriodChangeFromLabel = (label: string) => {
    const clean = label.trim();
    const found = (Object.entries(periodLabels) as [PeriodCode, string][])
      .find(([, v]) => v === clean)?.[0];

    if (!found) return;                   // 못 찾으면 아무 것도 안 함
    if (found === selectedPeriod) return; // 같은 기간을 또 누르면 무시
    setSelectedPeriod(found);
  };
  
  // PeriodSelector가 빈 값/초기값을 내려보내는 케이스에 대한 가드
  const handleDateRangeChange = (range?: string[] | null) => {
    if (!range || range.length !== 2 || !range[0] || !range[1]) {
      // 현재 period 기준으로 다시 계산
      setSelectedDateRange(rangeByPeriod(selectedPeriod));
      return;
    }
    setSelectedDateRange(range);
  };

  return (
    <>
      <Helmet>
        <title>Dashboard | IOPS SPACE</title>
        <meta name="description" content="This is the dashboard page." />
      </Helmet>
      <div className="flex h-full w-full flex-col">
        <div className="border-b border-border-primary">
          <div className="p-6">
            <Text size="heading2" weight="semibold" color="primary" className="mb-2">
              Dashboard
            </Text>
            <Text size="body2" weight="regular" color="secondary">
              {t('dashboard.description', { userName })}
              {nextScheduleDays !== null
                ? i18n.language === 'ko'
                  ? nextScheduleDays === 0
                    ? ` 예정된 위성 연결이 오늘입니다.`
                    : ` 예정된 위성 연결까지 ${nextScheduleDays}일 남았습니다.`
                  : nextScheduleDays === 0
                  ? ` Your scheduled satellite connection is today.`
                  : ` Only ${nextScheduleDays} days left until your scheduled satellite connection.`
                : ` ${t('dashboard.noUpcoming')}`}
            </Text>
          </div>
        </div>

        <div className="flex flex-1 flex-col p-6">
          <div className="mb-3">
            <div className="flex flex-row gap-2 p-1">
              <button
                onClick={() => setActiveTab(0)}
                className={cn(
                  'flex h-8 items-center gap-2 rounded-lg px-3 py-1 transition-colors',
                  activeTab === 0 ? 'bg-[#e8e8e8] text-zinc-600' : 'bg-transparent text-zinc-400 hover:bg-gray-100'
                )}
              >
                <CollectionOutlineIcon className="h-4 w-4" />
                <Text size="body2" weight="medium" color={activeTab === 0 ? 'primary' : 'tertiary'}>
                  {t('dashboard.tabs.monitoring')}
                </Text>
              </button>
              <button
                onClick={() => setActiveTab(1)}
                className={cn(
                  'flex h-8 items-center gap-2 rounded-lg px-3 py-1 transition-colors',
                  activeTab === 1 ? 'bg-[#e8e8e8] text-zinc-600' : 'bg-transparent text-zinc-400 hover:bg-gray-100'
                )}
              >
                <CreditCardOutlineIcon className="h-4 w-4" />
                <Text size="body2" weight="medium" color={activeTab === 1 ? 'primary' : 'tertiary'}>
                  {t('dashboard.tabs.paymentAnalytics')}
                </Text>
              </button>
            </div>
          </div>

          {activeTab === 0 ? (
            <div className="mb-6 flex flex-col gap-4 xl:flex-row">
              <div className="min-w-0 flex-[2] space-y-6">
                <SpacecraftCardList />
                <LatestScheduleSection />
                <DownlinkFileSection />
              </div>
              <div className="flex-[3] min-w-0">
                <EarthOrbitSection latestSpacecrafts={latestSpacecrafts} />
              </div>
            </div>
          ) : (
            <div className="relative w-full flex-1 space-y-8 rounded-xl border border-zinc-200 bg-white p-4">
              <PeriodSelector
                periodOptions={[
                  periodLabels['6M'],
                  periodLabels['1M'],
                  periodLabels['1W']
                ]}
                selectedPeriod={selectedPeriodLabel}
                onPeriodChange={handlePeriodChangeFromLabel}
                selectedDateRange={selectedDateRange}
                onDateRangeChange={handleDateRangeChange}  // ← 가드 적용
              />

              <div>
                <Text size="heading3" weight="semibold" className="mb-4 px-2">
                  {t('dashboard.paymentAnalytics.overview.title')}
                </Text>
                <OverviewSection data={overviewData} />
              </div>

              <div>
                <Text size="heading3" weight="semibold" className="mb-4 px-2">
                  {t('dashboard.paymentAnalytics.priceMonitor.title')}
                </Text>
                <PriceMonitorSection
                  satelliteData={satelliteData}
                  antennaData={antennaData}
                  paymentMethodData={paymentMethodData}
                  satelliteChartTitle={t('dashboard.paymentAnalytics.priceMonitor.costBySatellite')}
                  antennaChartTitle={t('dashboard.paymentAnalytics.priceMonitor.usageByAntenna')}
                  paymentMethodChartTitle={t('dashboard.paymentAnalytics.priceMonitor.usageByPayment')}
                  costTooltipLabel={t('dashboard.paymentAnalytics.priceMonitor.costTooltipLabel')}
                  currency={datasetCurrency} // 차트 툴팁/축 표기를 데이터 통화에 맞춤
                />
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default Dashboard;
